import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, Technique, TechniqueProgress } from "../lib/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Lock, CheckCircle2, Circle, AlertCircle } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { featureFlags } from "../config/featureFlags";

interface Track {
  id: string;
  name: string;
  description: string;
  techniques: Technique[];
}

export default function LearningPaths() {
  const [techniques, setTechniques] = useState<Technique[]>([]);
  const [progress, setProgress] = useState<TechniqueProgress[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [techniquesData, progressData] = await Promise.all([
          api.getTechniques({ view: "list" }), // Fetch minimal data for list view
          api.getUserProgress().catch(() => []), // Gracefully handle if no progress yet
        ]);
        setTechniques(techniquesData);
        setProgress(progressData);
      } catch (error) {
        console.error("Failed to load data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const getProgressForTechnique = (
    techniqueId: string,
  ): TechniqueProgress | undefined => {
    return progress.find((p) => p.technique_id === techniqueId);
  };

  const isTechniqueMastered = (techniqueId: string): boolean => {
    const prog = getProgressForTechnique(techniqueId);
    return prog ? ["advanced", "expert"].includes(prog.mastery_level) : false;
  };

  const arePrerequisitesMet = (technique: Technique): boolean => {
    if (!technique.prerequisites || technique.prerequisites.length === 0)
      return true;
    return technique.prerequisites.every((prereqId) =>
      isTechniqueMastered(prereqId),
    );
  };
  // @ts-ignore
  const getTechniqueByName = (name: string): Technique | undefined => {
    return techniques.find((t) => t.name === name);
  };

  const getMasteryIcon = (techniqueId: string) => {
    const prog = getProgressForTechnique(techniqueId);
    if (!prog) return <Circle className="w-5 h-5 text-gray-300" />;

    if (["advanced", "expert"].includes(prog.mastery_level)) {
      return <CheckCircle2 className="w-5 h-5 text-green-600" />;
    }

    return <AlertCircle className="w-5 h-5 text-yellow-600" />;
  };

  const getMasteryColor = (masteryLevel?: string) => {
    switch (masteryLevel) {
      case "expert":
        return "bg-purple-100 text-purple-800";
      case "advanced":
        return "bg-green-100 text-green-800";
      case "intermediate":
        return "bg-blue-100 text-blue-800";
      case "beginner":
        return "bg-yellow-100 text-yellow-800";
      case "novice":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getMasteryPercentage = (masteryLevel?: string): number => {
    switch (masteryLevel) {
      case "expert":
        return 100;
      case "advanced":
        return 80;
      case "intermediate":
        return 60;
      case "beginner":
        return 40;
      case "novice":
        return 20;
      default:
        return 0;
    }
  };

  const tracks: Track[] = [
    {
      id: "Mental Math Fundamentals",
      name: "Mental Math Fundamentals",
      description:
        "Master the basics of mental arithmetic with addition, subtraction, and simple multiplication tricks",
      techniques: techniques.filter((t) =>
        t.tracks?.includes("Mental Math Fundamentals"),
      ),
    },
    {
      id: "Multiplication Mastery",
      name: "Multiplication Mastery",
      description:
        "Advanced multiplication techniques from Vedic math and universal shortcuts",
      techniques: techniques.filter((t) =>
        t.tracks?.includes("Multiplication Mastery"),
      ),
    },
    {
      id: "Advanced Vedic Techniques",
      name: "Advanced Vedic Techniques",
      description:
        "Complex division and advanced calculation methods for expert mathematicians",
      techniques: techniques.filter((t) =>
        t.tracks?.includes("Advanced Vedic Techniques"),
      ),
    },
    {
      id: "Everyday Math Shortcuts",
      name: "Everyday Math Shortcuts",
      description:
        "Practical tricks for everyday calculations like percentages and quick estimates",
      techniques: techniques.filter((t) =>
        t.tracks?.includes("Everyday Math Shortcuts"),
      ),
    },
    ...(featureFlags.OLYMPIAD_TRACK
      ? [
          {
            id: "Math Olympiad - Problem Solving",
            name: "Math Olympiad - Problem Solving Strategies",
            description:
              "Master powerful problem-solving techniques for math competitions and Olympiads",
            techniques: techniques.filter(
              (t) =>
                t.tracks?.includes("Problem-Solving Strategies") ||
                t.tracks?.some((track) => track.includes("Problem")),
            ),
          },
          {
            id: "Math Olympiad - Number Theory",
            name: "Math Olympiad - Number Theory",
            description:
              "Explore prime numbers, divisibility, GCD, LCM, and the building blocks of mathematics",
            techniques: techniques.filter((t) =>
              t.tracks?.includes("Number Theory"),
            ),
          },
          {
            id: "Math Olympiad - Combinatorics",
            name: "Math Olympiad - Combinatorics",
            description:
              "Learn counting techniques, permutations, combinations, and the multiplication principle",
            techniques: techniques.filter((t) =>
              t.tracks?.includes("Combinatorics"),
            ),
          },
          {
            id: "Math Olympiad - Geometry",
            name: "Math Olympiad - Geometry & Proofs",
            description:
              "Master geometric theorems, triangle properties, circles, and formal proof writing",
            techniques: techniques.filter((t) =>
              t.tracks?.includes("Geometry & Proofs"),
            ),
          },
          {
            id: "Math Olympiad - Algebra",
            name: "Math Olympiad - Advanced Algebra",
            description:
              "Conquer quadratics, inequalities, polynomials, sequences, and series",
            techniques: techniques.filter((t) =>
              t.tracks?.includes("Advanced Algebra"),
            ),
          },
        ]
      : []),
  ];

  if (loading) {
    return <div className="container mx-auto px-4 py-8">Loading...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Learning Paths
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Follow curated tracks to master mental math systematically
        </p>
      </div>

      <div className="space-y-8">
        {tracks.map((track) => {
          if (track.techniques.length === 0) return null;

          const totalTechniques = track.techniques.length;
          const masteredTechniques = track.techniques.filter((t) =>
            isTechniqueMastered(t.id),
          ).length;
          const trackProgress =
            totalTechniques > 0
              ? (masteredTechniques / totalTechniques) * 100
              : 0;

          return (
            <Card key={track.id} className="overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-2xl mb-2">
                      {track.name}
                    </CardTitle>
                    <CardDescription className="text-base">
                      {track.description}
                    </CardDescription>
                  </div>
                  <Badge variant="secondary" className="ml-4">
                    {masteredTechniques}/{totalTechniques} Mastered
                  </Badge>
                </div>
                <div className="mt-4">
                  <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                    <span>Track Progress</span>
                    <span>{Math.round(trackProgress)}%</span>
                  </div>
                  <Progress value={trackProgress} className="h-2" />
                </div>
              </CardHeader>

              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {track.techniques.map((technique) => {
                    const techniqueProgress = getProgressForTechnique(
                      technique.id,
                    );
                    const prereqsMet = arePrerequisitesMet(technique);
                    const isLocked = !prereqsMet;

                    return (
                      <div
                        key={technique.id}
                        className={`border rounded-lg p-4 transition-all ${
                          isLocked
                            ? "bg-gray-50 dark:bg-gray-800/50 opacity-60"
                            : "hover:shadow-md hover:border-blue-300"
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="mt-1">
                            {getMasteryIcon(technique.id)}
                          </div>

                          <div className="flex-1">
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <h4 className="font-medium text-sm leading-tight">
                                {technique.name}
                              </h4>
                              {isLocked && (
                                <Lock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              )}
                            </div>

                            <div className="flex flex-wrap gap-1 mb-2">
                              {technique.level && (
                                <Badge
                                  variant="outline"
                                  className="text-xs bg-blue-50 text-blue-700"
                                >
                                  Level {technique.level}
                                </Badge>
                              )}
                              {techniqueProgress && (
                                <Badge
                                  variant="outline"
                                  className={`text-xs ${getMasteryColor(techniqueProgress.mastery_level)}`}
                                >
                                  {techniqueProgress.mastery_level}
                                </Badge>
                              )}
                              {technique.is_premium &&
                                featureFlags.SHOW_PRO_BADGES &&
                                featureFlags.PREMIUM_TECHNIQUES && (
                                  <Badge
                                    variant="secondary"
                                    className="text-xs"
                                  >
                                    <Lock className="w-3 h-3 mr-1" />
                                    PRO
                                  </Badge>
                                )}
                            </div>

                            {techniqueProgress && (
                              <Progress
                                value={getMasteryPercentage(
                                  techniqueProgress.mastery_level,
                                )}
                                className="h-1 mb-2"
                              />
                            )}

                            {technique.prerequisites &&
                              technique.prerequisites.length > 0 && (
                                <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                                  <span className="font-medium">Requires:</span>{" "}
                                  {technique.prerequisites.map(
                                    (prereqId, idx) => {
                                      const prereqTech = techniques.find(
                                        (t) => t.id === prereqId,
                                      );
                                      const prereqMastered =
                                        isTechniqueMastered(prereqId);
                                      return (
                                        <span key={prereqId}>
                                          <span
                                            className={
                                              prereqMastered
                                                ? "text-green-600"
                                                : "text-gray-600"
                                            }
                                          >
                                            {prereqTech?.name || "Unknown"}
                                            {prereqMastered && " ✓"}
                                          </span>
                                          {idx <
                                            technique.prerequisites!.length -
                                              1 && ", "}
                                        </span>
                                      );
                                    },
                                  )}
                                </div>
                              )}

                            <Link to={`/technique/${technique.id}`}>
                              <Button
                                size="sm"
                                variant={isLocked ? "outline" : "default"}
                                disabled={isLocked}
                                className="w-full mt-2"
                              >
                                {isLocked
                                  ? "Locked"
                                  : techniqueProgress
                                    ? "Continue"
                                    : "Start Learning"}
                              </Button>
                            </Link>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
